<script setup>
import { ref, onMounted, watch, nextTick } from 'vue'
import Button from 'primevue/button'
import Textarea from 'primevue/textarea'
import Message from 'primevue/message'
import { useChatStore } from '@/stores/messages/chat.js'
import { useAuthStore } from '@/stores/auth.js'
import { useConversationsStore } from '@/stores/messages/conversations.js'


const props = defineProps({ conversationId: { type: Number, required: true } })
const chat = useChatStore()
const auth = useAuthStore()
const conv = useConversationsStore()

const text = ref('')
const error = ref('')
const list = ref(null)

async function init() {
  await chat.init(props.conversationId)
  await nextTick(); scrollToBottom()
}

function scrollToBottom() { if (list.value) list.value.scrollTop = list.value.scrollHeight }
function onScroll() {
  if (!list.value) return
  const nearBottom = list.value.scrollHeight - list.value.scrollTop - list.value.clientHeight < 16
  if (nearBottom) chat.markRead()
}

async function send() {
  error.value = ''
  const body = String(text.value || '').trim()
  if (!body) { error.value = 'Введите текст'; return }
  try {
    await chat.send(body)
    text.value = ''
    await nextTick(); scrollToBottom()
  } catch { error.value = 'Не удалось отправить' }
}

onMounted(init)
watch(() => props.conversationId, init)

/**
 * @param {any} v
 * @returns {n|null}
 */
function toNum(v) {
  const n = Number(v)
  return Number.isFinite(n) ? n : null
}

function getSenderId(m) {
  // Приоритет: from_user.id — это то, что отдаёт ваш бэк
  return (
      toNum(m?.from_user?.id) ??
      toNum(m?.sender_id) ??
      toNum(m?.author_id) ??
      toNum(m?.from_id) ??
      toNum(m?.user_id) ??
      toNum(m?.sender?.id) ??
      toNum(m?.author?.id) ??
      null
  )
}

function isMine(m) {
  const myId = toNum(auth?.me?.id)
  if (!myId) {
    // Оптимистично отправленные сообщения (tmp_*) считаем своими
    if (typeof m?.id === 'string' && m.id.startsWith('tmp_')) return true
    return false
  }
  const sid = getSenderId(m)
  if (sid != null) return sid === myId
  if (typeof m?.id === 'string' && m.id.startsWith('tmp_')) return true
  return false
}

function authorLabel(m) {
  // Моё сообщение → «Вы»
  if (isMine(m)) return 'Вы'
  // Чужое сообщение → first_name из from_user
  return (
      m?.from_user?.first_name ||
      m?.from_user?.name ||
      // на всякий случай запасные варианты
      m?.sender?.first_name || m?.sender?.name ||
      m?.author?.first_name || m?.author?.name ||
      'Собеседник'
  )
}
</script>

<template>
  <div class="flex flex-col gap-2">
    <!-- Форма ОТВЕТА СВЕРХУ -->
    <form @submit.prevent="send" class="flex flex-col gap-2">
      <Textarea v-model="text" rows="3" autoResize placeholder="Ответить..." style="resize:none" />
      <Message v-if="error" severity="error" size="small" variant="simple">{{ error }}</Message>

        <Button type="submit" :disabled="chat.sending" severity="info" variant="outlined" rounded label="Отправить" />
    </form>

    <!-- Лист сообщений НИЖЕ формы -->
    <div ref="list" class="max-h-[50vh] overflow-auto pr-1" @scroll.passive="onScroll">
            <div v-for="m in chat.messages" :key="m.id" class="mb-2">
              <div class="text-xs text-gray-500">
                <span class="font-medium">{{ authorLabel(m) }}</span>
                <span> · {{ new Date(m.created_at).toLocaleString('ru-RU') }}</span>
              </div>
              <div class="whitespace-pre-line">{{ m.body }}</div>
      </div>
      <div class="py-2 text-center text-sm text-gray-500">
        <Button v-if="chat.hasMore && !chat.loading" size="small" variant="text" label="Загрузить ещё" @click="chat.fetchMore" />
        <span v-else-if="chat.loading">Загрузка…</span>
        <span v-else>Это все сообщения</span>
      </div>
    </div>
  </div>
</template>